import { setItem, getItem } from './localStorage'
import { AccountData } from '../modules/partner/action'
import { AccountData as AccountDataSelection } from '../modules/accountSelection/types'

// 5 days in seconds
const CACHE_TTL_SECONDS = 5 * 24 * 60 * 60 // 432,000 seconds

export interface CachedPartnerData {
  partnerId: string
  data: AccountData
  timestamp: number
}

export interface CachedAccountData {
  partnerId: string
  data: AccountDataSelection[]
  timestamp: number
}

/**
 * Generate cache key for partner data using first 5 characters of partner ID
 */
const getPartnerCacheKey = (partnerId: string): string => {
  return `labra_partner_data_${partnerId.slice(0, 5)}`
}

/**
 * Generate cache key for account data using first 5 characters of partner ID
 */
const getAccountCacheKey = (partnerId: string): string => {
  return `labra_account_data_${partnerId.slice(0, 5)}`
}

/**
 * Cache partner data with TTL
 */
export const setCachedPartnerData = async (
  partnerId: string,
  data: AccountData
): Promise<void> => {
  try {
    const cacheKey = getPartnerCacheKey(partnerId)
    const cachedData: CachedPartnerData = {
      partnerId,
      data,
      timestamp: Date.now(),
    }
    await setItem(cacheKey, cachedData, { ttlInSeconds: CACHE_TTL_SECONDS })
  } catch (error) {
    console.warn('[PartnerCache] Failed to cache partner data:', error)
  }
}

/**
 * Retrieve cached partner data
 */
export const getCachedPartnerData = async (
  partnerId: string
): Promise<AccountData | null> => {
  try {
    const cacheKey = getPartnerCacheKey(partnerId)
    const cachedData = (await getItem(cacheKey)) as CachedPartnerData | null

    if (cachedData && cachedData.partnerId === partnerId) {
      return cachedData.data
    }
    return null
  } catch (error) {
    console.warn(
      '[PartnerCache] Failed to retrieve cached partner data:',
      error
    )
    return null
  }
}

/**
 * Cache account data with TTL
 */
export const setCachedAccountData = async (
  partnerId: string,
  data: AccountDataSelection[]
): Promise<void> => {
  try {
    const cacheKey = getAccountCacheKey(partnerId)
    const cachedData: CachedAccountData = {
      partnerId,
      data,
      timestamp: Date.now(),
    }
    await setItem(cacheKey, cachedData, { ttlInSeconds: CACHE_TTL_SECONDS })
  } catch (error) {
    console.warn('[PartnerCache] Failed to cache account data:', error)
  }
}

/**
 * Retrieve cached account data
 */
export const getCachedAccountData = async (
  partnerId: string
): Promise<AccountDataSelection[] | null> => {
  try {
    const cacheKey = getAccountCacheKey(partnerId)
    const cachedData = (await getItem(cacheKey)) as CachedAccountData | null

    if (cachedData && cachedData.partnerId === partnerId) {
      return cachedData.data
    }
    return null
  } catch (error) {
    console.warn(
      '[PartnerCache] Failed to retrieve cached account data:',
      error
    )
    return null
  }
}

/**
 * Check if partner data exists in cache
 */
export const hasCachedPartnerData = async (
  partnerId: string
): Promise<boolean> => {
  const cachedData = await getCachedPartnerData(partnerId)
  return cachedData !== null
}

/**
 * Check if account data exists in cache
 */
export const hasCachedAccountData = async (
  partnerId: string
): Promise<boolean> => {
  const cachedData = await getCachedAccountData(partnerId)
  return cachedData !== null
}

/**
 * Clear all cached data for a specific partner
 */
export const clearPartnerCache = (partnerId: string): void => {
  try {
    const partnerCacheKey = getPartnerCacheKey(partnerId)
    const accountCacheKey = getAccountCacheKey(partnerId)

    localStorage.removeItem(partnerCacheKey)
    localStorage.removeItem(accountCacheKey)
  } catch (error) {
    console.warn('[PartnerCache] Failed to clear partner cache:', error)
  }
}

/**
 * Clear all partner and account cache data (for complete logout)
 */
export const clearAllPartnerCache = (): void => {
  try {
    const keysToRemove: string[] = []

    // Find all keys that match our cache patterns
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key &&
        (key.startsWith('labra_partner_data_') ||
          key.startsWith('labra_account_data_'))
      ) {
        keysToRemove.push(key)
      }
    }

    // Remove all found cache keys
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch (error) {
    console.warn('[PartnerCache] Failed to clear all partner cache:', error)
  }
}

/**
 * Get cache statistics for debugging
 */
export const getCacheStats = (): {
  partnerCacheKeys: string[]
  accountCacheKeys: string[]
  totalSize: number
} => {
  const partnerCacheKeys: string[] = []
  const accountCacheKeys: string[] = []
  let totalSize = 0

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        if (key.startsWith('labra_partner_data_')) {
          partnerCacheKeys.push(key)
          totalSize += localStorage.getItem(key)?.length || 0
        } else if (key.startsWith('labra_account_data_')) {
          accountCacheKeys.push(key)
          totalSize += localStorage.getItem(key)?.length || 0
        }
      }
    }
  } catch (error) {
    console.warn('[PartnerCache] Failed to get cache stats:', error)
  }

  return { partnerCacheKeys, accountCacheKeys, totalSize }
}
